import { evaluateCondition } from "./evaluatePageLogic";
import type { FormAnswerMap, PageLogicCondition, PageLogicOperator } from "./types";

const OPERATORS: readonly PageLogicOperator[] = [
    "equals",
    "not_equals",
    "includes",
    "greater_than",
    "greater_than_or_equals",
    "less_than",
    "less_than_or_equals",
];

/**
 * An input that may carry `inputs.show_when` (one page-logic condition that must hold for it to be shown)
 * or `inputs.options_from` (its choices are the checkboxes ticked earlier; hidden when none are).
 */
export interface ConditionalInput {
    input_id?: string | null;
    show_when?: unknown;
    options_from?: unknown;
}

/** One entry of `inputs.options_from`: offer choice `code` when checkbox `input_id` is ticked. */
export interface OptionsFromEntry {
    input_id: string;
    code: number;
}

/** Reads `inputs.options_from`. Malformed entries are dropped; null when there are none. */
export function parseOptionsFrom(raw: unknown): OptionsFromEntry[] | null {
    if (!Array.isArray(raw)) return null;
    const entries = raw.flatMap((entry) => {
        if (!entry || typeof entry !== "object") return [];
        const { input_id, code } = entry as Partial<OptionsFromEntry>;
        if (typeof input_id !== "string" || input_id === "" || typeof code !== "number" || !Number.isFinite(code)) {
            return [];
        }
        return [{ input_id, code }];
    });
    return entries.length > 0 ? entries : null;
}

function isTickedAnswer(value: FormAnswerMap[string]): boolean {
    return value === true || value === "true" || value === 1;
}

/**
 * The choice codes an options_from input offers for these answers: the codes whose checkbox is ticked,
 * in options_from order, without repeats. Null for an input without options_from (all its choices apply).
 */
export function allowedOptionCodes(input: ConditionalInput, answers: FormAnswerMap): number[] | null {
    const entries = parseOptionsFrom(input.options_from);
    if (!entries) return null;
    const codes: number[] = [];
    for (const entry of entries) {
        if (isTickedAnswer(answers[entry.input_id]) && !codes.includes(entry.code)) codes.push(entry.code);
    }
    return codes;
}

/** Whether the input's visibility depends on other answers (show_when or options_from). */
export function hasVisibilityCondition(input: ConditionalInput): boolean {
    return parseShowWhen(input.show_when) !== null || parseOptionsFrom(input.options_from) !== null;
}

/** Reads `inputs.show_when`. A missing or malformed condition means the input is always shown. */
export function parseShowWhen(raw: unknown): PageLogicCondition | null {
    if (!raw || typeof raw !== "object") return null;
    const condition = raw as Partial<PageLogicCondition>;
    if (typeof condition.input_id !== "string" || condition.input_id === "") return null;
    if (!condition.operator || !OPERATORS.includes(condition.operator)) return null;
    const value = condition.value;
    if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") return null;
    return { input_id: condition.input_id, operator: condition.operator, value };
}

/** Whether the input is shown for these answers (the current page's values plus earlier answers). */
export function isInputShown(input: ConditionalInput, answers: FormAnswerMap): boolean {
    const condition = parseShowWhen(input.show_when);
    if (condition && !evaluateCondition(condition, answers)) return false;
    const codes = allowedOptionCodes(input, answers);
    return codes === null || codes.length > 0;
}

/**
 * Inputs hidden by their show_when condition, or by options_from when no listed checkbox is ticked. An answer to a hidden input does not count, so an input
 * that depends on a hidden one is worked out without that answer (repeated until nothing changes).
 */
export function findHiddenInputIds(inputs: readonly ConditionalInput[], answers: FormAnswerMap): string[] {
    const conditional = inputs.filter((input) => input.input_id && hasVisibilityCondition(input));
    let hidden = new Set<string>();
    for (let round = 0; round <= conditional.length; round++) {
        const visibleAnswers = Object.fromEntries(Object.entries(answers).filter(([id]) => !hidden.has(id)));
        const next = new Set(
            conditional.filter((input) => !isInputShown(input, visibleAnswers)).map((input) => input.input_id!)
        );
        if (next.size === hidden.size && [...next].every((id) => hidden.has(id))) break;
        hidden = next;
    }
    return [...hidden];
}
